// Activity service - tracks and retrieves user activities
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc,
  setDoc, 
  updateDoc,
  query, 
  where, 
  orderBy, 
  limit,
  getDocs,
  onSnapshot,
  serverTimestamp,
  Timestamp,
  startAfter,
  getCountFromServer
} from 'firebase/firestore';

/**
 * Normalize wallet address
 */
const normalize = (addr) => {
  if (!addr || typeof addr !== 'string') {
    throw new Error('Invalid address: must be a non-empty string');
  }
  let normalized = addr.toLowerCase().trim();
  if (!normalized.startsWith('0x')) {
    normalized = '0x' + normalized;
  }
  return normalized;
};

/**
 * Activity types
 */
export const ACTIVITY_TYPES = {
  USER_JOINED: 'user_joined',
  GROUP_POST: 'group_post',
  GROUP_EVENT: 'group_event',
  PROFILE_UPDATE: 'profile_update',
  MESSAGE_STARTED: 'message_started',
  GROUP_JOINED: 'group_joined'
};

/**
 * Record an activity
 * @param {string} type - Activity type (from ACTIVITY_TYPES)
 * @param {string} actorAddress - Wallet address of the user performing the action
 * @param {Object} data - Activity-specific data
 * @returns {Promise<string>} Activity ID
 */
export const recordActivity = async (type, actorAddress, data = {}) => {
  if (!type || !actorAddress) {
    throw new Error('Activity type and actor address are required');
  }

  const actor = normalize(actorAddress);
  const activityId = `activity_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

  const activityDoc = {
    activityId,
    type,
    actor: actor,
    data: data,
    createdAt: serverTimestamp(),
    timestamp: Date.now() // For client-side sorting
  };

  const activitiesRef = collection(db, 'activities');
  const activityRef = doc(activitiesRef, activityId);
  await setDoc(activityRef, activityDoc);

  // Also update user's last activity timestamp
  await updateUserLastActivity(actor);

  return activityId;
};

/**
 * Update user's last activity timestamp
 * @param {string} userAddress - Wallet address
 */
const updateUserLastActivity = async (userAddress) => {
  try {
    const user = normalize(userAddress);
    const userActivityRef = doc(db, 'userActivity', user);
    await setDoc(userActivityRef, {
      lastActivityAt: serverTimestamp(),
      lastActivityTimestamp: Date.now()
    }, { merge: true });
  } catch (error) {
    console.error('Error updating user last activity:', error);
  }
};

/**
 * Get user's last activity timestamp
 * @param {string} userAddress - Wallet address
 * @returns {Promise<number|null>} Timestamp in milliseconds or null
 */
export const getUserLastActivity = async (userAddress) => {
  if (!userAddress) return null;

  try {
    const user = normalize(userAddress);
    const userActivityRef = doc(db, 'userActivity', user);
    const userActivityDoc = await getDoc(userActivityRef);
    
    if (userActivityDoc.exists()) {
      const data = userActivityDoc.data();
      return data.lastActivityTimestamp || null;
    }
    return null;
  } catch (error) {
    console.error('Error getting user last activity:', error);
    return null;
  }
};

/**
 * Check if user is recently active (within last 24 hours)
 * @param {string} userAddress - Wallet address
 * @returns {Promise<boolean>} True if recently active
 */
export const isUserRecentlyActive = async (userAddress) => {
  const lastActivity = await getUserLastActivity(userAddress);
  if (!lastActivity) return false;
  
  const now = Date.now();
  const twentyFourHours = 24 * 60 * 60 * 1000;
  return (now - lastActivity) < twentyFourHours;
};

/**
 * Get activity feed for a user
 * @param {string} userAddress - Wallet address of the user viewing the feed
 * @param {Object} options - Options for filtering
 * @param {number} options.limitCount - Maximum number of activities to retrieve
 * @param {Array<string>} options.activityTypes - Filter by activity types (optional)
 * @param {Object} options.location - User's location {lat, lng} for nearby filtering (optional)
 * @param {number} options.nearbyRadiusKm - Radius in km for nearby filtering (default: 50)
 * @returns {Promise<Array>} Array of activities
 */
export const getActivityFeed = async (userAddress, options = {}) => {
  if (!userAddress) return [];

  const {
    limitCount = 50,
    activityTypes = null,
    location = null,
    nearbyRadiusKm = 50
  } = options;

  try {
    const activitiesRef = collection(db, 'activities');
    let q = query(activitiesRef);

    // Filter by activity types if specified
    if (activityTypes && activityTypes.length > 0) {
      // Firestore doesn't support OR queries easily, so we'll filter client-side
      // For now, we'll get all and filter
    }

    // Order by creation date (newest first)
    q = query(q, orderBy('timestamp', 'desc'));

    // Limit results
    q = query(q, limit(limitCount * 2)); // Get more than needed for filtering

    const querySnapshot = await getDocs(q);
    const activities = [];

    querySnapshot.forEach((doc) => {
      const activityData = doc.data();
      
      // Filter by activity types if specified
      if (activityTypes && activityTypes.length > 0) {
        if (!activityTypes.includes(activityData.type)) {
          return; // Skip this activity
        }
      }

      activities.push({
        id: doc.id,
        ...activityData
      });
    });

    // Sort by timestamp (newest first) - client-side for reliability
    activities.sort((a, b) => {
      const aTime = a.timestamp || (a.createdAt?.toMillis?.() || (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0) || 0);
      const bTime = b.timestamp || (b.createdAt?.toMillis?.() || (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0) || 0);
      return bTime - aTime;
    });

    // Apply limit after filtering
    return activities.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting activity feed:', error);
    throw error;
  }
};

/**
 * Subscribe to activity feed
 * @param {string} userAddress - Wallet address of the user viewing the feed
 * @param {Function} callback - Callback function that receives array of activities
 * @param {Object} options - Options for filtering
 * @returns {Function} Unsubscribe function
 */
export const subscribeToActivityFeed = (userAddress, callback, options = {}) => {
  if (!userAddress) {
    callback([]);
    return () => {};
  }

  const {
    limitCount = 50,
    activityTypes = null
  } = options;

  try {
    const activitiesRef = collection(db, 'activities');
    let q = query(activitiesRef);

    // Order by timestamp (newest first)
    q = query(q, orderBy('timestamp', 'desc'));

    // Limit results
    q = query(q, limit(limitCount * 2));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const activities = [];
        querySnapshot.forEach((doc) => {
          const activityData = doc.data();
          
          // Filter by activity types if specified
          if (activityTypes && activityTypes.length > 0) {
            if (!activityTypes.includes(activityData.type)) {
              return; // Skip this activity
            }
          }

          activities.push({
            id: doc.id,
            ...activityData
          });
        });

        // Sort by timestamp (newest first)
        activities.sort((a, b) => {
          const aTime = a.timestamp || (a.createdAt?.toMillis?.() || (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0) || 0);
          const bTime = b.timestamp || (b.createdAt?.toMillis?.() || (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0) || 0);
          return bTime - aTime;
        });

        callback(activities.slice(0, limitCount));
      },
      (error) => {
        console.error('Error subscribing to activity feed:', error);
        callback([]);
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error('Error setting up activity feed subscription:', error);
    callback([]);
    return () => {};
  }
};

/**
 * Get activities for nearby users (users who joined nearby)
 * @param {Object} userLocation - User's location {lat, lng}
 * @param {number} radiusKm - Radius in km (default: 50)
 * @param {number} limitCount - Maximum number of activities
 * @returns {Promise<Array>} Array of activities
 */
export const getNearbyUserActivities = async (userLocation, radiusKm = 50, limitCount = 20) => {
  if (!userLocation || !userLocation.lat || !userLocation.lng) {
    return [];
  }

  try {
    // Get all user_joined activities and filter by location client-side
    // For now, we'll get recent user_joined activities and check their locations
    const activitiesRef = collection(db, 'activities');
    const q = query(
      activitiesRef,
      where('type', '==', ACTIVITY_TYPES.USER_JOINED),
      orderBy('timestamp', 'desc'),
      limit(limitCount * 3) // Get more to filter by location
    );

    const querySnapshot = await getDocs(q);
    const activities = [];

    // Calculate distance for each activity
    querySnapshot.forEach((doc) => {
      const activityData = doc.data();
      const activityLocation = activityData.data?.location;
      
      if (activityLocation && activityLocation.lat && activityLocation.lng) {
        const distance = calculateDistance(
          userLocation.lat,
          userLocation.lng,
          activityLocation.lat,
          activityLocation.lng
        );

        if (distance <= radiusKm) {
          activities.push({
            id: doc.id,
            ...activityData,
            distance: distance
          });
        }
      }
    });

    // Sort by distance (closest first)
    activities.sort((a, b) => a.distance - b.distance);

    return activities.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting nearby user activities:', error);
    throw error;
  }
};

/**
 * Calculate distance between two coordinates (Haversine formula)
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in kilometers
 */
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the Earth in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Get activities for user's connections (people they've messaged with)
 * @param {string} userAddress - Wallet address
 * @param {number} limitCount - Maximum number of activities
 * @returns {Promise<Array>} Array of activities
 */
export const getConnectionActivities = async (userAddress, limitCount = 20) => {
  if (!userAddress) return [];

  try {
    // Get user's conversations to find connections
    const conversationsRef = collection(db, 'conversations');
    const user = normalize(userAddress);
    
    // Get conversations where user is a participant
    const q1 = query(
      conversationsRef,
      where('participants', 'array-contains', user),
      limit(100)
    );

    const querySnapshot = await getDocs(q1);
    const connections = new Set();
    
    querySnapshot.forEach((doc) => {
      const conversation = doc.data();
      if (conversation.participants) {
        conversation.participants.forEach(participant => {
          if (participant.toLowerCase() !== user) {
            connections.add(participant.toLowerCase());
          }
        });
      }
    });

    if (connections.size === 0) {
      return [];
    }

    // Get activities from connections
    const activitiesRef = collection(db, 'activities');
    const activities = [];

    // Get activities for each connection (limited query per connection)
    const connectionArray = Array.from(connections).slice(0, 20); // Limit to 20 connections
    
    for (const connection of connectionArray) {
      const q2 = query(
        activitiesRef,
        where('actor', '==', connection),
        orderBy('timestamp', 'desc'),
        limit(5) // Get 5 most recent per connection
      );

      try {
        const connectionActivities = await getDocs(q2);
        connectionActivities.forEach((doc) => {
          activities.push({
            id: doc.id,
            ...doc.data()
          });
        });
      } catch (error) {
        // If orderBy fails (missing index), skip this connection
        console.warn(`Could not get activities for connection ${connection}:`, error);
      }
    }

    // Sort by timestamp (newest first)
    activities.sort((a, b) => {
      const aTime = a.timestamp || (a.createdAt?.toMillis?.() || (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0) || 0);
      const bTime = b.timestamp || (b.createdAt?.toMillis?.() || (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0) || 0);
      return bTime - aTime;
    });

    return activities.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting connection activities:', error);
    throw error;
  }
};

